/**
 * SYSTEM SETTINGS MODULE — public embed keys (`pos_embed_keys`).
 *
 * TWO KINDS OF KEY, ONE TABLE — and the file keeps the table's name for the
 * same reason the table kept it (migration 0097): everything a POS key needs
 * from a key — the opaque `public_key`, the origin allowlist, `is_active`, the
 * per-key caps, the RLS policy — means exactly the same thing for a sample
 * request form. A second table (or a second service) would duplicate all of it
 * and then drift from it.
 *
 *   kind='pos'    — backlog item 28. One key per distributor. The ship-to is
 *                   DERIVED from `delivery_contact_id`, so the submitter has no
 *                   field that influences where the goods go, and one
 *                   distributor cannot order as another (migration 0075).
 *                   `delivery_contact_id` is REQUIRED.
 *
 *   kind='sample' — backlog item 124. A sample usually goes somewhere new — a
 *                   prospect, an event, an account not set up yet — so the
 *                   ship-to is FREE TEXT typed by the requester and
 *                   `delivery_contact_id` is always NULL. What replaces the
 *                   derived-address invariant is the approval gate on
 *                   `product_sample_requests` (embed-sourced rows are forced to
 *                   `approval_status='pending'` and cannot reach 'Shipped'
 *                   until a human rules) plus the tighter per-key ceilings
 *                   below. A leaked sample key fills a review queue; it does
 *                   not empty a warehouse.
 *
 * Both of those are CHECK constraints in 0097, not conventions:
 *   pos_embed_keys_contact_by_kind_check   (kind ⇄ delivery_contact_id)
 *   pos_embed_keys_sample_ceilings_check   (sample caps)
 * A constraint violation reaches the user as an unreadable Postgres string, so
 * this layer refuses to compose a row the database would reject.
 *
 * Placement follows `retailSourceService.js`: a configuration surface for
 * another module's table lives in System Settings, because "set up the form my
 * distributors order through" is workspace-admin configuration and belongs next
 * to the rest of it. `pos_embed_keys` is owned by pos-fulfillment and this
 * relationship is recorded in that module's ARCHITECTURE.md.
 *
 * Writes are field-whitelisted. `public_key`, `account_id`, `id` and the
 * timestamps are DB-owned and never accepted from a caller — in particular the
 * key itself cannot be chosen, only regenerated (which is how you revoke one
 * that leaked). `kind` is accepted on INSERT ONLY: see SAVEABLE below.
 */

import { supabase } from "../../../shared/utils/supabaseClient.js";
import { getMyPrimaryAccount } from "../../../shared/utils/accountService.js";

const KINDS = new Set(["pos", "sample"]);

/** What each kind is called in a message the tenant reads. */
const NOUN = { pos: "order form", sample: "sample request form" };
const NOUN_PLURAL = { pos: "order forms", sample: "sample request forms" };

/** The bundle each kind's snippet points at. Both ship from `embed/dist`. */
const BUNDLE = { pos: "pos-order.js", sample: "sample-request.js" };

// notify_email is the brand-side "an order arrived" address (backlog APP-86,
// migration 0087). It is OPT-IN and empty by default — an existing key keeps
// behaving exactly as it did. It must appear in this explicit SELECT list or
// the panel cannot even tell the column exists and shows "Not available yet".
// It applies to BOTH kinds: for a sample key it is the more useful of the two,
// because a request that nobody looks at is a request that never ships.
const SELECT =
  "id, public_key, kind, label, delivery_contact_id, allowed_origins, is_active, " +
  "max_lines, max_qty_per_line, max_orders_per_hour, notify_email, created_at, updated_at";

// UPDATE whitelist. `kind` is deliberately ABSENT: it decides WHICH invariant
// protects the key — a derived ship-to for 'pos', an approval gate for 'sample'
// — so flipping it on a live key would swap the guarantee under an endpoint
// that is already pasted on someone's website. A POS key turned 'sample' would
// also fail `pos_embed_keys_contact_by_kind_check` on its own bound contact.
// Kind is chosen once, at creation; to change it, revoke the key and mint a new
// one, which is also the only honest way to tell the tenant their embed changed.
const SAVEABLE = [
  "label", "delivery_contact_id", "allowed_origins", "is_active",
  "max_lines", "max_qty_per_line", "max_orders_per_hour", "notify_email",
];

// INSERT whitelist = the above plus `kind`.
const SAVEABLE_INSERT = ["kind", ...SAVEABLE];

// Per-kind bounds. The sample numbers are the ceilings 0097 enforces, restated
// here so an over-large value gets a sentence the tenant can act on instead of
// being silently clamped by the trigger (`least()`) or, if the trigger is ever
// disabled, rejected by the CHECK with a constraint name.
//
// The POS defaults (500 units/line, 25 lines) are sized for point-of-sale
// material a distributor pays for indirectly. The same numbers against FREE
// PRODUCT going to a caller-chosen address are not a cap in any useful sense,
// which is why sample keys get their own, much lower ones.
const LIMIT_BOUNDS = {
  pos: {
    max_lines:           [1, 100],
    max_qty_per_line:    [1, 10000],
    max_orders_per_hour: [1, 200],
  },
  sample: {
    max_lines:           [1, 10],
    max_qty_per_line:    [1, 100],
    max_orders_per_hour: [1, 10],
  },
};

/**
 * Bounds for a kind, for a UI that wants to show `max=` before saving. Copied
 * on the way out — these are the numbers `clean()` validates against, and a
 * caller that mutated them would move the check, not just its label.
 */
export function embedLimitBounds(kind = "pos") {
  const b = LIMIT_BOUNDS[normalizeKind(kind)];
  return Object.fromEntries(Object.entries(b).map(([k, v]) => [k, [...v]]));
}

function normalizeKind(kind) {
  const k = String(kind ?? "pos");
  if (!KINDS.has(k)) throw new Error("Unknown form type.");
  return k;
}

let _acct = null;
async function acctId() {
  if (_acct) return _acct;
  const a = await getMyPrimaryAccount();
  if (!a?.accountId) throw new Error("No workspace found for your account.");
  _acct = a.accountId;
  return _acct;
}

/** Reset the cached account (sign-out / workspace switch). */
export function resetPosEmbedAccount() { _acct = null; }

/**
 * An origin is a scheme + host, nothing else — `https://shop.example.com`.
 * A path or a trailing slash silently never matches the browser's `Origin`
 * header, which would look like "the form is broken" rather than "the origin
 * is wrong", so it is normalised here and rejected if it can't be.
 */
export function normalizeOrigin(raw) {
  const s = String(raw ?? "").trim();
  if (!s) return null;
  let u;
  try {
    u = new URL(s.includes("://") ? s : `https://${s}`);
  } catch {
    throw new Error(`"${s}" isn't a valid website address.`);
  }
  if (u.protocol !== "https:" && u.protocol !== "http:") {
    throw new Error("A website address must start with https://");
  }
  return u.origin;
}

function clean(patch = {}, kind = "pos", { insert = false } = {}) {
  const k0 = normalizeKind(kind);
  const bounds = LIMIT_BOUNDS[k0];
  const fields = insert ? SAVEABLE_INSERT : SAVEABLE;
  const out = {};
  for (const k of fields) {
    if (k === "kind") {
      // Only reachable on INSERT (SAVEABLE omits it), and the value comes from
      // the caller's declared kind rather than the patch, so a stray
      // `{ kind: 'pos' }` in a body cannot disagree with the row being built.
      out.kind = k0;
      continue;
    }
    if (patch[k] === undefined) continue;
    if (k === "label") {
      const v = String(patch[k] ?? "").trim();
      if (!v) throw new Error("Give the key a name so you can tell it from the others.");
      out[k] = v.slice(0, 120);
    } else if (k === "allowed_origins") {
      const list = Array.isArray(patch[k]) ? patch[k] : [];
      out[k] = [...new Set(list.map(normalizeOrigin).filter(Boolean))].slice(0, 20);
    } else if (k === "is_active") {
      out[k] = !!patch[k];
    } else if (k === "delivery_contact_id") {
      // A sample key is CHECK-constrained to a NULL delivery contact (0097): a
      // bound address would be a second, derived ship-to path alongside the
      // free-text one, which is exactly how the derived one stops being
      // reliably true. Forced null rather than passed through, so no caller can
      // compose a row the database will reject with a constraint name.
      out[k] = k0 === "sample" ? null : (patch[k] || null);
    } else if (k === "notify_email") {
      // A typo here fails silently — the write still succeeds and the notice
      // just never arrives, which is indistinguishable from the feature being
      // off. So the format is checked even though the column does not care.
      const v = String(patch[k] ?? "").trim().toLowerCase();
      if (!v) {
        // ⚠️ REQUIRED for a sample key, optional for a POS key, and the
        // asymmetry is not fussiness. A POS order lands in a queue that gets
        // worked whether or not anyone was emailed. A SAMPLE request from the
        // embed is DB-forced to approval_status='pending' (0097) and CANNOT
        // ship until a human rules on it — so with no address, the only signal
        // that someone is waiting is a screen nobody has a reason to open, and
        // the request dies quietly. Free product that never ships and a
        // prospect who never hears back is the worse failure.
        if (k0 === "sample") {
          throw new Error(
            "Add an email address for sample requests. A request from this form waits for " +
            "your approval before anything ships, so without somewhere to send the alert " +
            "nobody will know it arrived.",
          );
        }
        out[k] = null;
        continue;
      }
      if (v.length > 320 || !/^[^\s@]+@[^\s@.]+\.[^\s@]+$/.test(v)) {
        throw new Error(
          k0 === "sample"
            ? "That doesn't look like an email address, and a sample request form needs one."
            : "That doesn't look like an email address. Leave it blank for no notification.",
        );
      }
      out[k] = v;
    } else {
      const [lo, hi] = bounds[k];
      const n = Number(patch[k]);
      if (!Number.isInteger(n) || n < lo || n > hi) {
        throw new Error(`That limit must be a whole number between ${lo} and ${hi}.`);
      }
      out[k] = n;
    }
  }
  // The loop above `continue`s on an ABSENT field, so the sample-key rule in the
  // notify_email branch only fires when the caller sent an empty one. On INSERT
  // a body that omits the key entirely would slip past it — and "I never typed
  // it" is the likelier way to end up without an alert address than "I cleared
  // it". Check presence here, where absence is visible.
  if (insert && k0 === "sample" && !out.notify_email) {
    throw new Error(
      "Add an email address for sample requests. A request from this form waits for " +
      "your approval before anything ships, so without somewhere to send the alert " +
      "nobody will know it arrived.",
    );
  }
  return out;
}

export async function listEmbedKeys(kind = "pos") {
  const k0 = normalizeKind(kind);
  const acct = await acctId();
  // Filtered by kind, not just by account: the two kinds are configured on two
  // different setup screens, and a POS screen listing sample keys would offer a
  // delivery-contact edit the CHECK constraint refuses.
  //
  // Range-walked. No brand has 1,000 distributors, so PostgREST's default cap
  // is not reachable in practice — but this is the exact read shape that was
  // silently truncating every grid in the app before backlog item 11, and
  // paginating by default costs nothing.
  const { data, error } = await supabase
    .from("pos_embed_keys").select(SELECT)
    .eq("account_id", acct)
    .eq("kind", k0)
    .order("created_at", { ascending: true })
    .range(0, 999);
  if (error) {
    console.error("[posEmbedService.listEmbedKeys]", error);
    throw new Error(`Could not load the ${NOUN[k0]} keys.`);
  }
  return data ?? [];
}

export async function createEmbedKey(fields = {}) {
  const k0 = normalizeKind(fields.kind ?? "pos");
  const acct = await acctId();
  const row = { account_id: acct, ...clean(fields, k0, { insert: true }) };
  if (k0 === "pos" && !row.delivery_contact_id) {
    throw new Error("Pick the distributor this form is for.");
  }
  const { data, error } = await supabase
    .from("pos_embed_keys").insert(row).select(SELECT).single();
  if (error) {
    console.error("[posEmbedService.createEmbedKey]", error);
    throw new Error(`Could not create the ${NOUN[k0]} key.`);
  }
  return data;
}

/**
 * `kind` is the kind of the key being edited, not a change to it — it selects
 * the limit bounds and the wording, and `clean()` refuses to put it in the
 * UPDATE payload regardless of what the patch contains.
 */
export async function updateEmbedKey(id, fields, kind = "pos") {
  const k0 = normalizeKind(kind);
  const acct = await acctId();
  const { data, error } = await supabase
    .from("pos_embed_keys").update(clean(fields, k0))
    .eq("id", id).eq("account_id", acct)
    .select(SELECT).single();
  if (error) {
    console.error("[posEmbedService.updateEmbedKey]", error);
    throw new Error(`Could not save the ${NOUN[k0]} key.`);
  }
  return data;
}

/**
 * One order form per distributor, generated in a single pass. POS ONLY —
 * there is no equivalent for sample keys, because a sample form is not per
 * distributor: it is per place you want to publish it (one on the trade site,
 * one behind a rep portal), and there is no list to generate that from.
 *
 * Onboarding a brand with 40 distributors one row at a time is the kind of
 * setup friction that gets a module abandoned before it's used. The delivery
 * address book is already the exact list they'd be copying from, so this just
 * fills the gap: a key for every contact that hasn't got one.
 *
 * Idempotent — contacts that already have a key are skipped, so pressing it
 * again after adding a distributor creates only the missing one. Never touches
 * an existing key (its allowed origins and limits are the tenant's settings, not
 * ours to reset).
 */
export async function generateMissingEmbedKeys(contacts = []) {
  const acct = await acctId();
  const existing = await listEmbedKeys("pos");
  const covered = new Set(existing.map((k) => k.delivery_contact_id));

  const missing = contacts.filter((c) => c?.id && !covered.has(c.id));
  if (missing.length === 0) return { created: 0, skipped: contacts.length };

  const rows = missing.map((c) => ({
    account_id: acct,
    kind: "pos",
    delivery_contact_id: c.id,
    label: [c.distributor, c.warehouse_name, c.state].filter(Boolean).join(" — ").slice(0, 120)
           || "Order form",
    // allowed_origins stays EMPTY on purpose: a brand-new form must be switched
    // off until someone names the site it belongs on. Bulk-creating 40 live
    // endpoints because a button was pressed would be the wrong default.
  }));

  const { data, error } = await supabase
    .from("pos_embed_keys").insert(rows).select("id");
  if (error) {
    console.error("[posEmbedService.generateMissingEmbedKeys]", error);
    throw new Error(`Could not generate the missing ${NOUN_PLURAL.pos}.`);
  }
  return { created: data?.length ?? 0, skipped: contacts.length - missing.length };
}

export async function deleteEmbedKey(id, kind = "pos") {
  const k0 = normalizeKind(kind);
  const acct = await acctId();
  const { error } = await supabase
    .from("pos_embed_keys").delete().eq("id", id).eq("account_id", acct);
  if (error) {
    console.error("[posEmbedService.deleteEmbedKey]", error);
    throw new Error(`Could not delete the ${NOUN[k0]} key.`);
  }
  return true;
}

/**
 * The snippet the customer pastes. Built from the embed host, which is the same
 * one the distribution-map embed ships from — all three bundles live in
 * `embed/dist` and are deployed together.
 *
 * The BUNDLE differs by kind and that is not cosmetic: `pos-order.js` renders
 * the POS catalog and no address fields at all, `sample-request.js` renders the
 * sample catalog and the free-text ship-to. Pointing a sample key at the POS
 * bundle produces a form that collects nothing the server will accept.
 */
export function embedSnippet(publicKey, kind = "pos") {
  // Item 206: the fallback used to read app.brandcommand.app/embed/embed.js.
  // Nothing is served there — the app host answers every unknown path with the
  // SPA's index.html at Content-Type: text/html, so a <script src> pointing at
  // it loads HTML and dies on a syntax error rather than 404ing visibly.
  // deploy.mjs --embed uploads every bundle in embed/dist to EMBED_REMOTE_DIR,
  // which is the MARKETING root (/brandcommand.app), so this is the real host.
  const bundle = BUNDLE[KINDS.has(String(kind)) ? String(kind) : "pos"];
  const src = (import.meta.env?.VITE_EMBED_SRC || "https://brandcommand.app/embed.js")
    .replace(/embed\.js$/, bundle);
  return `<script src="${src}" data-key="${publicKey}"></script>`;
}
